import { useState, useRef } from "react";

export const findSelectedLineIndex = (items = [], selected = {}) => {
  if (selected?.lineId) {
    const byLine = items.findIndex((item) => item.lineId === selected.lineId);
    if (byLine !== -1) return byLine;
  }
  return items.findIndex((item) => item.productId === selected?.productId);
};

/**
 * useSalesRepresentative
 *
 * Manages the sales representative modal state, search filtering,
 * and assignment of sales reps to order-level and line-item level.
 */
const useSalesRepresentative = ({
  cart,
  setCart,
  tillData,
  selectedProductInCart,
  setOpenSalesRepRef,
}) => {
  const salesReprestiveList = tillData.tillAccess.csBunit.salesRep;

  const [openSalesRep, setOpenSalesRep] = useState({ flag: false, level: "" });
  const [salesRepModalOpen, setSalesRepModalOpen] = useState({
    status: false,
    title: "",
  });
  const [salesRepresent, setSalesRepresent] = useState({});
  const [salesRepresentDefaultLine, setSalesRepresentDefaultLine] = useState({ salesRepresentId: null, name: null });
  const [salesRepresentSearchInput, setSalesRepresentSearchInput] = useState("");
  const [filteredSalesRepresentList, setFilteredSalesRepresentList] = useState(salesReprestiveList);
  const [salesRepValue, setSalesRepValue] = useState("");
  const salesRepRef = useRef(null);

  // Wire the ref so productOps can call setOpenSalesRep via forward-ref pattern
  if (setOpenSalesRepRef) {
    setOpenSalesRepRef.current = setOpenSalesRep;
  }

  const handleSalesRepresentive = (record) => {
    if (salesRepModalOpen.title === "orderSalesRep") {
      setSalesRepresentDefaultLine({ ...record });
      setCart({
        ...cart,
        salesRepId: record.salesRepresentId,
      });
      localStorage.setItem(
        "cartObj",
        JSON.stringify({
          ...cart,
          salesRepId: record.salesRepresentId,
        })
      );
      setSalesRepresent(record);
    } else if (salesRepModalOpen.title === "itemSalesRep" && selectedProductInCart && Object.keys(selectedProductInCart).length > 0) {
      setSalesRepresentDefaultLine({ ...record });
      // Match the selected line by lineId: two lines of the same product (e.g. the
      // same style in two sizes, or a split line) must each keep their own rep.
      // productId is only a fallback for legacy lines that predate lineId.
      const lineItemsIndex = findSelectedLineIndex(cart.items, selectedProductInCart);
      if (lineItemsIndex === -1) {
        setSalesRepModalOpen({ status: false, title: "" });
        return;
      }
      const lineItemsData = cart.items.map((item, i) =>
        i === lineItemsIndex ? { ...item, salesRepId: record.salesRepresentId, salesRepName: record.name } : item
      );

      setCart({
        ...cart,
        items: lineItemsData,
      });
      localStorage.setItem(
        "cartObj",
        JSON.stringify({
          ...cart,
          items: lineItemsData,
        })
      );
    }
    setSalesRepModalOpen({ status: false, title: "" });
  };

  const handleSalesRepresentSearchInput = (value) => {
    if (value !== "") {
      const results = salesReprestiveList.filter((list) => {
        return list.name.toLowerCase().startsWith(value.toLowerCase());
      });
      setFilteredSalesRepresentList(results);
    } else {
      setFilteredSalesRepresentList(salesReprestiveList);
    }
    setSalesRepresentSearchInput(value);
  };

  return {
    openSalesRep,
    setOpenSalesRep,
    salesRepModalOpen,
    setSalesRepModalOpen,
    salesRepresent,
    setSalesRepresent,
    salesRepresentDefaultLine,
    setSalesRepresentDefaultLine,
    salesRepresentSearchInput,
    setSalesRepresentSearchInput,
    salesRepValue,
    setSalesRepValue,
    salesRepRef,
    salesReprestiveList,
    filteredSalesRepresentList,
    setFilteredSalesRepresentList,
    handleSalesRepresentive,
    handleSalesRepresentSearchInput,
  };
};

export default useSalesRepresentative;
